import { AsyncPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SubstitutionParameterOptionsInterface } from '@vendasta/templates';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { VariableMenuItemSelectorComponent } from '../variable-menu-item-selector/variable-menu-item-selector.component';
import { VariableMenuItem, VariableMenuService } from '../variable-menu.service';

export const SALESPERSON_CUSTOM_DATA = 'salesPersonCustomData';
export const BUSINESS_CUSTOM_DATA = 'businessCustomData';

@Component({
  imports: [VariableMenuItemSelectorComponent, AsyncPipe, TranslateModule, MatButtonModule, NgIf],
  selector: 'app-variable-menu-dialog',
  templateUrl: './variable-menu-dialog.component.html',
  styleUrls: ['./variable-menu-dialog.component.scss'],
})
// VariableMenuDialogComponent is used both as a regular component as well as in an iFrame. This is because it is used
// with a TinyMCE editor and the only way for it to render angular components is through an iframe.
// Because of this the input parameters can either come in via @Input's or via query params
export class VariableMenuDialogComponent {
  @Input() emitEventsInsteadOfTakingAction = false;
  @Input() partnerId = '';
  @Input() marketId = '';
  @Input() isOrderAutomation = false;
  @Input() extraMenuItems: VariableMenuItem[] = [];
  @Input() supportedSubstitutionOptions: SubstitutionParameterOptionsInterface = null;
  @Input() dontIncludeDefaultCategories = false;
  @Output() formResult: EventEmitter<string | null> = new EventEmitter<string | null>();

  chosenVariableMenutItem: VariableMenuItem = null;

  includeHtml$: Observable<boolean> = this.route.queryParamMap.pipe(
    map((params) => {
      return !!params.get('includeHtml');
    }),
  );

  partnerId$: Observable<string> = this.route.queryParamMap.pipe(
    map((params) => {
      if (params.get('partnerId')) {
        this.partnerId = params.get('partnerId');
      }
      return this.partnerId;
    }),
  );

  marketId$: Observable<string> = this.route.queryParamMap.pipe(
    map((params) => {
      if (params.get('marketId')) {
        this.marketId = params.get('marketId');
      }
      return this.marketId;
    }),
  );

  extraMenuItems$: Observable<VariableMenuItem[]> = this.route.queryParamMap.pipe(
    map((params) => {
      return params.get('extraMenuItemsCookieString');
    }),
    map((cookieId) => {
      if (cookieId) {
        const payload = this.cookieService.get(cookieId);
        if (payload) {
          // Observables can't be json stringified so they are serilizaed as raw values and need to be changed back to
          // observables so the rest of the code doesn't have to worry about this
          return this.convertNonObservablesSubItemsToObservables(JSON.parse(payload));
        }
      }
      return this.extraMenuItems;
    }),
  );

  variables$: Observable<VariableMenuItem[]> = combineLatest([
    this.includeHtml$,
    this.extraMenuItems$,
    this.partnerId$,
    this.marketId$,
  ]).pipe(
    map(([includeHtml, extraMenuItems, partnerId, marketId]) => {
      return this.variableMenuService.getVariables(
        partnerId,
        marketId,
        {
          includeHtml: includeHtml,
          forRequiredOptions: this.supportedSubstitutionOptions,
          dontIncludeDefaultCategories: this.dontIncludeDefaultCategories,
        },
        extraMenuItems,
      );
    }),
    map((vars) => {
      return vars.filter((v) => {
        if (v?.settings?.requiredParams) {
          return v.settings.requiredParams.reduce((prev, curr, i, arr) => {
            // If there is a required parameter, get the value from the properties of this instance of
            // VariableMenuDialogComponent to see if it will be provided
            return prev && !!this[arr[i]];
          }, true);
        } else {
          return true;
        }
      });
    }),
    shareReplay(1),
  );

  constructor(
    private readonly variableMenuService: VariableMenuService,
    private readonly route: ActivatedRoute,
    private readonly cookieService: CookieService,
  ) {}

  private convertNonObservablesSubItemsToObservables(toConvert: VariableMenuItem[]): VariableMenuItem[] {
    return toConvert.map((item) => {
      item.subItems$ = of(this.convertNonObservablesSubItemsToObservables(item.subItems$ as any));
      return item;
    });
  }

  add(): void {
    const content = this.chosenVariableMenutItem?.settings?.shouldNotSurroundWithCurlyBraces
      ? this.chosenVariableMenutItem.replacementTag
      : '{{ ' + this.chosenVariableMenutItem.replacementTag + ' }}';
    if (this.emitEventsInsteadOfTakingAction) {
      this.formResult.emit(content);
    } else {
      window.parent.postMessage({
        mceAction: 'insertContent',
        content: content,
      });
      this.close();
    }
  }

  close(): void {
    if (this.emitEventsInsteadOfTakingAction) {
      this.formResult.emit(null);
    } else {
      window.parent.postMessage({ mceAction: 'close' });
    }
  }

  variableMenuItemSelected(variableMenuItem: VariableMenuItem): void {
    this.chosenVariableMenutItem = variableMenuItem;
  }
}
